import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import './MetadataEditModal.scss';

const MetadataEditModal = ({ show, file, onClose, onSave }) => {
    const [editedData, setEditedData] = useState({});
    // What the artwork box shows. A picture the user just uploaded carries raw
    // bytes (`data`) and is encoded here once. The file's own picture arrives
    // from the analyzer as a small table thumbnail (`dataUrl`, see
    // fixMetadata.js artworkThumbnail) — shown at once, then swapped for the
    // full-size art from the audio server's /album-art route when it loads.
    const [previewUrl, setPreviewUrl] = useState(null);
    useEffect(() => {
        const art = editedData.artwork;
        if (!art) { setPreviewUrl(null); return undefined; }
        if (art.data) {
            let bin = '';
            const bytes = new Uint8Array(art.data);
            for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
            setPreviewUrl(`data:${art.format || 'image/jpeg'};base64,${btoa(bin)}`);
            return undefined;
        }
        setPreviewUrl(art.dataUrl || null);
        if (!art.dataUrl || !file?.filePath || !window.electron?.getAlbumArtUrl) return undefined;
        let alive = true;
        window.electron.getAlbumArtUrl(file.filePath).then((url) => {
            if (!alive || !url) return;
            const img = new Image();
            img.onload = () => { if (alive) setPreviewUrl(url); };
            img.src = url;
        }).catch(() => {});
        return () => { alive = false; };
    }, [editedData.artwork, file]);

    useEffect(() => {
        if (file) {
            // Initialize with current values from the file
            setEditedData({
                artist: file.current.artist || '',
                title: file.current.title || '',
                album: file.current.album || '',
                albumArtist: file.current.albumArtist || '',
                label: file.current.label || '',
                releaseDate: file.current.releaseDate || '',
                genre: file.current.genre || '',
                bpm: file.current.bpm || '',
                key: file.current.key || '',
                track: file.current.track || '',
                composer: file.current.composer || '',
                discNumber: file.current.discNumber || '',
                artwork: file.current.artwork || null,
            });
        }
    }, [file]);

    const handleChange = (field, value) => {
        setEditedData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleArtworkUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (event) => {
                const arrayBuffer = event.target.result;
                const uint8Array = new Uint8Array(arrayBuffer);

                // Convert to format expected by the backend
                const artwork = {
                    data: uint8Array,
                    format: file.type || 'image/jpeg',
                    type: 3, // Front cover
                    description: 'Front cover'
                };

                setEditedData(prev => ({
                    ...prev,
                    artwork: artwork
                }));
            };
            reader.readAsArrayBuffer(file);
        }
    };

    const handleRemoveArtwork = () => {
        setEditedData(prev => ({
            ...prev,
            artwork: null
        }));
    };

    const handleSave = () => {
        onSave(file.filePath, editedData);
        onClose();
    };

    if (!file) return null;

    return (
        <Modal show={show} onHide={onClose} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>Edit Metadata - {file.fileName}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {/* Enter in a text field must SAVE, never submit: an un-prevented
                    submit navigates the page (full app reload, edit lost), and the
                    browser's implicit submission also clicks the form's default
                    submit button — which used to be the artwork <button> (raw
                    buttons default to type="submit"), popping the file picker on
                    every Enter. Enter is handled here; submit stays a no-op. */}
                <Form
                    onSubmit={(e) => e.preventDefault()}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && e.target.tagName === 'INPUT' && e.target.type !== 'file') {
                            e.preventDefault();
                            handleSave();
                        }
                    }}
                >
                    <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                        {previewUrl ? (
                            <div>
                                <img
                                    src={previewUrl}
                                    alt="Album artwork"
                                    style={{
                                        width: '150px',
                                        height: '150px',
                                        objectFit: 'cover',
                                        borderRadius: '8px',
                                        border: '1px solid #ddd',
                                        marginBottom: '10px'
                                    }}
                                />
                                <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                                    <button
                                        type="button"
                                        onClick={handleRemoveArtwork}
                                        style={{
                                            background: 'rgba(255, 87, 87, 0.1)',
                                            border: '1px solid rgba(255, 87, 87, 0.3)',
                                            borderRadius: '6px',
                                            padding: '8px 12px',
                                            cursor: 'pointer',
                                            color: '#FF5757',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            fontSize: '13px',
                                            transition: 'all 0.2s'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.target.style.background = 'rgba(255, 87, 87, 0.2)';
                                            e.target.style.borderColor = 'rgba(255, 87, 87, 0.5)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.background = 'rgba(255, 87, 87, 0.1)';
                                            e.target.style.borderColor = 'rgba(255, 87, 87, 0.3)';
                                        }}
                                        title="Remove Artwork"
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M3 6h18M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                                            <line x1="10" y1="11" x2="10" y2="17" />
                                            <line x1="14" y1="11" x2="14" y2="17" />
                                        </svg>
                                        Remove
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => document.getElementById('artwork-upload').click()}
                                        style={{
                                            background: 'rgba(76, 194, 241, 0.1)',
                                            border: '1px solid rgba(76, 194, 241, 0.3)',
                                            borderRadius: '6px',
                                            padding: '8px 12px',
                                            cursor: 'pointer',
                                            color: '#4CC2F1',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            fontSize: '13px',
                                            transition: 'all 0.2s'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.target.style.background = 'rgba(76, 194, 241, 0.2)';
                                            e.target.style.borderColor = 'rgba(76, 194, 241, 0.5)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.background = 'rgba(76, 194, 241, 0.1)';
                                            e.target.style.borderColor = 'rgba(76, 194, 241, 0.3)';
                                        }}
                                        title="Change Artwork"
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                        </svg>
                                        Change
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div>
                                <div style={{
                                    width: '150px',
                                    height: '150px',
                                    margin: '0 auto 10px',
                                    border: '2px dashed #ddd',
                                    borderRadius: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#999'
                                }}>
                                    No Artwork
                                </div>
                                <button
                                    type="button"
                                    onClick={() => document.getElementById('artwork-upload').click()}
                                    style={{
                                        background: 'rgba(76, 194, 241, 0.1)',
                                        border: '1px solid rgba(76, 194, 241, 0.3)',
                                        borderRadius: '6px',
                                        padding: '10px 16px',
                                        cursor: 'pointer',
                                        color: '#4CC2F1',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        fontSize: '13px',
                                        fontWeight: '600',
                                        transition: 'all 0.2s',
                                        margin: '0 auto'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.background = 'rgba(76, 194, 241, 0.2)';
                                        e.target.style.borderColor = 'rgba(76, 194, 241, 0.5)';
                                        e.target.style.transform = 'translateY(-1px)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.background = 'rgba(76, 194, 241, 0.1)';
                                        e.target.style.borderColor = 'rgba(76, 194, 241, 0.3)';
                                        e.target.style.transform = 'translateY(0)';
                                    }}
                                    title="Upload Artwork"
                                >
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                        <polyline points="17 8 12 3 7 8" />
                                        <line x1="12" y1="3" x2="12" y2="15" />
                                    </svg>
                                    Upload Artwork
                                </button>
                            </div>
                        )}
                        <input
                            id="artwork-upload"
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            onChange={handleArtworkUpload}
                        />
                    </div>
                    <div className="metadata-edit-grid">
                        <Form.Group>
                            <Form.Label>Artist</Form.Label>
                            <Form.Control
                                type="text"
                                value={editedData.artist || ''}
                                onChange={(e) => handleChange('artist', e.target.value)}
                                placeholder="Artist name"
                            />
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>Title</Form.Label>
                            <Form.Control
                                type="text"
                                value={editedData.title || ''}
                                onChange={(e) => handleChange('title', e.target.value)}
                                placeholder="Track title"
                            />
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>Album</Form.Label>
                            <Form.Control
                                type="text"
                                value={editedData.album || ''}
                                onChange={(e) => handleChange('album', e.target.value)}
                                placeholder="Album name"
                            />
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>Album Artist</Form.Label>
                            <Form.Control
                                type="text"
                                value={editedData.albumArtist || ''}
                                onChange={(e) => handleChange('albumArtist', e.target.value)}
                                placeholder="Album artist"
                            />
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>Label</Form.Label>
                            <Form.Control
                                type="text"
                                value={editedData.label || ''}
                                onChange={(e) => handleChange('label', e.target.value)}
                                placeholder="Record label"
                            />
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>Release Date</Form.Label>
                            <Form.Control
                                type="text"
                                value={editedData.releaseDate || ''}
                                onChange={(e) => handleChange('releaseDate', e.target.value)}
                                placeholder="YYYY-MM-DD or YYYY"
                            />
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>Genre</Form.Label>
                            <Form.Control
                                type="text"
                                value={editedData.genre || ''}
                                onChange={(e) => handleChange('genre', e.target.value)}
                                placeholder="Genre"
                            />
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>BPM</Form.Label>
                            <Form.Control
                                type="text"
                                value={editedData.bpm || ''}
                                onChange={(e) => handleChange('bpm', e.target.value)}
                                placeholder="Tempo (BPM)"
                            />
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>Key</Form.Label>
                            <Form.Control
                                type="text"
                                value={editedData.key || ''}
                                onChange={(e) => handleChange('key', e.target.value)}
                                placeholder="Musical key"
                            />
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>Track #</Form.Label>
                            <Form.Control
                                type="text"
                                value={editedData.track || ''}
                                onChange={(e) => handleChange('track', e.target.value)}
                                placeholder="Track number"
                            />
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>Composer</Form.Label>
                            <Form.Control
                                type="text"
                                value={editedData.composer || ''}
                                onChange={(e) => handleChange('composer', e.target.value)}
                                placeholder="Composer"
                            />
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>Disc #</Form.Label>
                            <Form.Control
                                type="text"
                                value={editedData.discNumber || ''}
                                onChange={(e) => handleChange('discNumber', e.target.value)}
                                placeholder="Disc number"
                            />
                        </Form.Group>
                    </div>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleSave}>
                    Save Changes
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default MetadataEditModal;
